import { useMemo } from 'react';
import { MoreFiltersMenu, type MoreMenuCategory } from '@odo/shared-ui';
import { COMMUNICATION_CATEGORIES } from '../practice/communications/categories';
import { CHANNELS } from '../practice/communications/channels';

// The "More" menu for the cross-practice Communications page: Status,
// Channel, Category. Practice/Assignee are pinned pills instead.

const STATUS_OPTIONS = [
    { value: 'open', label: 'Open' },
    { value: 'resolved', label: 'Resolved' }
];

const CHANNEL_OPTIONS = CHANNELS.map((c) => ({ value: c.id, label: c.label }));

interface CommunicationsMoreFiltersMenuProps {
    statusFilter: string[];
    onStatusFilterChange: (selected: string[]) => void;
    channelFilter: string[];
    onChannelFilterChange: (selected: string[]) => void;
    categoryFilter: string | null;
    onCategoryFilterChange: (value: string | null) => void;
}

export function CommunicationsMoreFiltersMenu({
    statusFilter,
    onStatusFilterChange,
    channelFilter,
    onChannelFilterChange,
    categoryFilter,
    onCategoryFilterChange
}: CommunicationsMoreFiltersMenuProps) {
    // Status, channel and category are all single-value on the endpoint, but
    // the More menu speaks in string[] - so each keeps the "newest selection
    // wins" rule rather than accumulating a set the API would drop.
    const moreCategories: MoreMenuCategory[] = useMemo(() => {
        const single = (
            current: string[],
            selected: string[],
            apply: (value: string | null) => void
        ) => {
            const added = selected.filter((value) => !current.includes(value));
            apply(added.length > 0 ? added[added.length - 1] : null);
        };
        return [
            {
                key: 'status',
                label: 'Status',
                options: STATUS_OPTIONS,
                selectedOptions: statusFilter,
                onChange: (selected) =>
                    single(statusFilter, selected, (v) => onStatusFilterChange(v ? [v] : []))
            },
            {
                key: 'channel',
                label: 'Channel',
                options: CHANNEL_OPTIONS,
                selectedOptions: channelFilter,
                onChange: (selected) =>
                    single(channelFilter, selected, (v) => onChannelFilterChange(v ? [v] : []))
            },
            {
                key: 'category',
                label: 'Category',
                options: COMMUNICATION_CATEGORIES.map((c) => ({ value: c.id, label: c.id })),
                selectedOptions: categoryFilter ? [categoryFilter] : [],
                onChange: (selected) =>
                    single(categoryFilter ? [categoryFilter] : [], selected, (v) =>
                        onCategoryFilterChange(v)
                    )
            }
        ];
    }, [
        statusFilter,
        onStatusFilterChange,
        channelFilter,
        onChannelFilterChange,
        categoryFilter,
        onCategoryFilterChange
    ]);

    const hiddenActiveCount = moreCategories.filter((c) => c.selectedOptions.length > 0).length;

    return (
        <MoreFiltersMenu
            categories={moreCategories}
            allCategories={moreCategories}
            hiddenActiveCount={hiddenActiveCount}
        />
    );
}
